import React, { useCallback } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { SECOND_MS } from 'data/constants'
import { GetSigner } from 'privy/WalletReady'
import { SignerUndefinedError, useMyUserId, useWithdrawTransaction } from 'use-towns-client'

export type WithdrawArgs = {
    depositIds: bigint[]
    getSigner: GetSigner
}

export const useWithdraw = () => {
    const userId = useMyUserId()

    const { withdraw, ...transactionRest } = useWithdrawTransaction()

    return {
        ...transactionRest,
        withdraw: useCallback(
            async (args: WithdrawArgs) => {
                const { depositIds, getSigner } = args
                const signer = await getSigner()
                if (!signer) {
                    throw new SignerUndefinedError()
                }

                toasts.submitting()

                const txnContext = await withdraw({
                    depositIds,
                    signer,
                    userId: userId ? (userId as `0x${string}`) : undefined,
                    onError: (error) => {
                        toasts.error(error)
                    },
                    onSuccess: () => {
                        toasts.success()
                    },
                })

                return txnContext
            },
            [userId, withdraw],
        ),
    }
}

const toasts = {
    submitting: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Pending toast={toast} message="Processing withdrawal..." />
            ),
            {
                id: 'withdraw',
                duration: Number.POSITIVE_INFINITY,
            },
        )
    },

    success: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message="Withdrawal successful!"
                    subMessage="Your tokens have been withdrawn successfully."
                />
            ),
            {
                id: 'withdraw',
                duration: SECOND_MS * 4,
            },
        )
    },

    error: (error: Error) => {
        console.error(error.message)
        popupToast(
            ({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Withdrawal failed"
                    subMessage={error.message}
                />
            ),
            {
                id: 'withdraw',
                duration: SECOND_MS * 5,
            },
        )
    },
}
